<template>
	<article class="d-self-service-list-custom">
		<PageHero
			:title="header.title"
			:description="header.description"
			v-bind="{ breadcrumb }"
		/>

		<input
			v-for="(item, index) in searchFilters.fields.filter(
				(e) => e.type === 'Hidden'
			)"
			:key="`hidden-${index}`"
			type="hidden"
			:name="item.name"
			:value="item.value"
		/>

		<div class="bg-primary pb-xl px-layout-margin">
			<div
				:class="[
					'flex flex-wrap flex-col gap-x-0 gap-y-sm mb-lg',
					'>=1024:flex-row >=1024:items-center >=1024:gap-x-layout-gutter',
				]"
			>
				<BaseInputRadio
					:value="computedFilterValues.targetgroup"
					:config="computedFilters.targetgroup"
					class="w-full min-w-fit mb-sm >=1024:mb-0 >=1024:w-4col"
					@input="(event) => updateValue('targetgroup', event)"
				/>
				<BaseInputDropdown
					class="w-full min-w-fit >=1024:w-4col flex-grow"
					:value="computedFilterValues.category"
					:config="computedFilters.category"
					:aria-label="computedFilters.category.placeholder"
					@input="(event) => updateValue('category', event)"
				/>
				<BaseInputText
					v-model="textValue"
					class="w-full >=1024:w-4col flex-grow"
					:placeholder="computedFilters.text.placeholder"
					@keypress.enter="() => updateValue('text', textValue)"
				>
					<template #button>
						<button
							:class="[
								'absolute top-1/2 right-8',
								'transform -translate-y-1/2',
								'rounded-full bg-extra w-40 h-40',
								'flex justify-center items-center',
							]"
							aria-label="Søg efter nyheder"
							@click="() => updateValue('text', textValue)"
						>
							<SvgSearch class="w-16 h-16 text-white" />
						</button>
					</template>
				</BaseInputText>
			</div>

			<LimboSearch
				v-slot="{ data, pagination, state, action }"
				:search-filters="filters"
				:config="{
					enableLiveSearch: true,
					allowSearchFiltersMutation: true,
				}"
				class="relative"
			>
				<Transition
					:duration="200"
					mode="out-in"
					name="t-self-service-list-custom__content"
				>
					<div
						v-if="state.isLoading"
						key="self-service-list-custom__loader"
					>
						<SearchLoader class="mx-auto w-md text-extra" />
					</div>

					<div
						v-if="!state.isLoading"
						key="self-service-list-custom__content"
					>
						<div
							v-if="pagination && pagination.total === 0"
							class="text-center w-full col-start-1 col-end-5"
							v-text="'Ingen resultater fundet'"
						></div>

						<div v-if="data && data.length > 0" class="space-y-xl">
							<div
								:class="[
									'grid grid-cols-1 >=768:grid-cols-2 >=1024:grid-cols-3 >=1440:grid-cols-4',
									'gap-x-layout-gutter gap-y-md',
								]"
							>
								<ContentCard
									v-for="(item, index) in data"
									:key="`item-${index}`"
									:to="item.url"
									:target="item.target"
									:name="item.title"
									light
								>
									<template #before>
										<span
											v-text="
												item.targetGroups.join(', ')
											"
										></span>
									</template>
								</ContentCard>
							</div>

							<div
								v-if="
									pagination.total >
									pagination.limit + pagination.offset
								"
								class="w-full flex justify-center"
							>
								<button
									:class="[
										'flex items-center space-x-12 mx-auto',
										'uppercase text-button font-semibold',
										'w-fit text-text hover:text-primary-button',
										'group',
									]"
									@click="action.fetchMore"
								>
									<span
										:class="[
											'flex-shrink-0',
											'duration-200 ease-smooth-out',
										]"
										v-text="'Vis flere'"
									></span>

									<SvgCaret
										:class="[
											'w-12 transform rotate-90',
											'duration-200 ease-smooth-out',
											'group-hover:translate-y-2',
										]"
									/>
								</button>
							</div>
						</div>
					</div>
				</Transition>
			</LimboSearch>
		</div>

		<FeedbackForm v-if="activateFeedback" class="mb-lg" />
	</article>
</template>

<script>
import SvgSearch from '~/assets/svgs/search-icon.svg?inline';
import SvgCaret from '~/assets/svgs/caret-icon.svg?inline';
import LimboSearch from '~/citi-baseline/components/LimboSearch';
import PageHero from '~/components/main/PageHero';
import ContentCard from '~/components/shared/ContentCard';
import SearchLoader from '~/components/shared/SearchLoader';
import FeedbackForm from '~/components/shared/FeedbackForm';

export default {
	name: 'MainSelfServiceListCustom',

	components: {
		SvgSearch,
		SvgCaret,
		LimboSearch,
		PageHero,
		ContentCard,
		SearchLoader,
		FeedbackForm,
	},

	inheritAttrs: false,

	props: {
		header: {
			type: Object,
			required: true,
		},
		breadcrumb: {
			type: Array,
			required: false,
		},
		searchFilters: {
			type: Object,
			required: true,
		},
		activateFeedback: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			filters: this.searchFilters,
			textValue:
				this.searchFilters?.fields?.find((e) => e.name === 'text')
					.value || '',
		};
	},

	computed: {
		computedFilters() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = element;
			});

			return response;
		},
		computedFilterValues() {
			const response = {};
			this.searchFilters?.fields?.forEach((element) => {
				response[element.name] = Array.isArray(element.value)
					? element.value.find((e) => e.checked)?.value
					: element.value;
			});

			return response;
		},
	},

	watch: {
		'computedFilterValues.text'(value) {
			this.textValue = value;
		},
	},

	methods: {
		updateValue(name, event) {
			const field = this.filters.fields.find((e) => e.name === name);

			if (typeof event === 'string' && Array.isArray(field.value)) {
				field.value.forEach((element) => {
					element.checked = element.value === event;
				});
			} else if (typeof event === 'string') {
				field.value = event;
			} else {
				field.value = event?.target?.value ?? '';
			}
		},
	},
};
</script>

<style lang="postcss">
.t-self-service-list-custom__content-enter-active,
.t-self-service-list-custom__content-leave-active {
	@apply duration-200 ease-smooth-out;
}

.t-self-service-list-custom__content-enter,
.t-self-service-list-custom__content-leave-to {
	@apply opacity-0;
}
</style>
